<template>
  <div>
    <!-- Date filter -->
    <div class="flex items-center gap-4 mb-4 flex-wrap">
      <div class="flex items-center gap-2 text-sm">
        <label class="font-medium text-gray-700">From:</label>
        <input v-model="fromDate" type="date" class="px-3 py-2 border rounded-md" />
      </div>
      <div class="flex items-center gap-2 text-sm">
        <label class="font-medium text-gray-700">To:</label>
        <input v-model="toDate" type="date" class="px-3 py-2 border rounded-md" />
      </div>
      <button @click="fetchPublished"
        class="px-5 py-2 bg-custom-yellow text-custom-blue font-semibold rounded hover:bg-yellow-400 text-sm">
        Search
      </button>
      <button v-if="fromDate || toDate" @click="clearFilter"
        class="text-sm text-red-600 hover:text-red-800 font-medium">
        Clear
      </button>
    </div>

    <div v-if="showLoader" class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-yellow-500"></div>
    </div>

    <div v-else class="relative overflow-x-auto shadow-md sm:rounded-sm">
      <table class="w-full text-left text-gray-700">
        <thead>
          <tr class="bg-gray-100">
            <th class="py-3 px-6">#</th>
            <th class="py-3 px-6">Visit Code</th>
            <th class="py-3 px-6">Patient Name</th>
            <th class="py-3 px-6">Patient Unique Code</th>
            <th class="py-3 px-6">Tests</th>
            <th class="py-3 px-6">Requesting Doctor</th>
            <th class="py-3 px-6">Visit Date/Time</th>
            <th class="py-3 px-6">Action</th>
          </tr>
        </thead>
        <tbody class="text-gray-600 text-sm">
          <tr v-for="(visit, index) in paginatedData" :key="visit.VisitId"
            class="border-b border-gray-200 hover:bg-gray-100">
            <td class="py-3 px-6">{{ (currentPage - 1) * itemsPerPage + index + 1 }}</td>
            <td class="py-3 px-6">{{ visit.VisitCode }}</td>
            <td class="py-3 px-6">{{ visit.patient.FullName }}</td>
            <td class="py-3 px-6">{{ visit.patient.unique_code }}</td>
            <td class="py-3 px-6">
              <span v-for="t in visit.lab_tests" :key="t.id"
                class="inline-block bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full mr-1 mb-0.5">
                {{ t.test_type.name }}
              </span>
            </td>
            <td class="py-3 px-6">{{ visit.lab_tests[0]?.requested_by?.Username || '—' }}</td>
            <td class="py-3 px-6 whitespace-nowrap">{{ formatDate(visit.VisitStartTime) }}</td>
            <td class="py-3 px-6">
              <button @click="viewLabRequest(visit.VisitId)"
                class="bg-custom-yellow hover:bg-orange-600 text-white font-bold py-1 px-3 rounded text-sm">
                View Report
              </button>
            </td>
          </tr>
          <tr v-if="!showLoader && !results.length">
            <td colspan="8" class="py-10 text-center text-gray-400">
              No published results found for the selected period.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <PaginationComponent
      v-model:currentPage="currentPage"
      :totalPages="totalPages"
      @update:currentPage="(p) => currentPage = p"
    />
  </div>
</template>

<script>
import apiClient from '@/services/api'
import PaginationComponent from '@/components/shared/pagination.vue'

export default {
  name: 'PublishedLabResultsTab',
  components: { PaginationComponent },
  data() {
    const today = new Date().toISOString().split('T')[0]
    return {
      showLoader: false,
      results: [],
      fromDate: today,
      toDate: today,
      currentPage: 1,
      itemsPerPage: 10,
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.results.length / this.itemsPerPage) || 1
    },
    paginatedData() {
      const start = (this.currentPage - 1) * this.itemsPerPage
      return this.results.slice(start, start + this.itemsPerPage)
    },
  },
  mounted() {
    this.fetchPublished()
  },
  methods: {
    async fetchPublished() {
      this.showLoader = true
      try {
        const params = {}
        if (this.fromDate) params.from_date = this.fromDate
        if (this.toDate)   params.to_date   = this.toDate
        const res = await apiClient.listPublishedLabTests(params)
        if (res.success) this.results = res.data
        else this.$toast.error(res.message || 'Failed to load published results')
      } catch (e) {
        this.$toast.error('Network error')
      } finally {
        this.showLoader = false
      }
    },
    clearFilter() {
      this.fromDate = ''
      this.toDate = ''
      this.fetchPublished()
    },
    viewLabRequest(visitId) {
      this.$router.push('/lab-request-details/' + visitId)
    },
    formatDate(str) {
      if (!str) return '—'
      return new Date(str).toLocaleString('en-KE', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    },
  },
}
</script>
